import React, { useId } from 'react';
import { noop } from 'lodash-es';

import StateMessages from 'common/components/Input/StatefulInput/StateMessages';
import { FieldChangeTrigger, HTMLAutoCompleteAttribute } from 'common/enums/form';
import { useKeyboardSelect } from 'common/hooks/keyboard';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { FormGroupData } from 'common/types/field';
import type { FieldChangeTriggerHandler } from 'common/types/form';
import { useDrawer } from '../../hooks/drawer';
import { useFetchDrawerOrders, useOrderSummary } from '../../hooks/orders-drawer';
import { selectDrawerContentTrackingEvent } from '../../utils/tracking';
import { FieldName } from '../enums';
import Drawer from '../Fields/common/Drawer';
import { OrderList } from '../OrderDrawer/OrderList';
import DrawerInput from './common/DrawerInput';

interface OrderNumberDrawerProps<FieldName extends string = string> {
  name: FieldName;
  label: string;
  value: string;
  required?: boolean;
  disabled?: boolean;
  reason: string;
  onChange: FieldChangeTriggerHandler<FieldName>;
  setDrawerError: () => void;
  hasError?: boolean;
  errors?: string[];
}

export type OrderNumberDrawerFormData = FormGroupData<OrderNumberDrawerProps>;

const OrderNumberDrawer = ({
  name,
  value = '',
  label,
  disabled,
  required,
  onChange,
  setDrawerError = noop,
  reason,
  hasError: hasErrorProp,
  errors,
}: OrderNumberDrawerProps) => {
  const { t } = useLocalization();
  const stateMessagesId = useId();
  const { isOpen, openDrawer, closeDrawer } = useDrawer('order', reason);

  const { orders, isLoading, fetchMore, hasMoreOrders, hasFetchError } = useFetchDrawerOrders({
    setDrawerError,
  });

  const orderSummary = useOrderSummary(value, orders);
  const onKeyDown = useKeyboardSelect(openDrawer);

  const setOrder = (orderNumber: string, orderId: string) => {
    selectDrawerContentTrackingEvent(reason, 'order');
    onChange({
      fieldName: name,
      value: orderNumber,
      trigger: FieldChangeTrigger.Change,
    });
    onChange({
      fieldName: FieldName.OrderId,
      value: orderId,
      trigger: FieldChangeTrigger.Change,
    });
  };

  const onBlur = () => {
    onChange({
      fieldName: name,
      value: value ?? '',
      trigger: FieldChangeTrigger.Blur,
    });
  };

  const hasError = !isOpen && hasErrorProp;

  return (
    <>
      <DrawerInput
        name={name}
        label={label}
        onChange={noop}
        disabled={disabled}
        required={required}
        onClick={openDrawer}
        onBlur={onBlur}
        hasError={hasError}
        onKeyDown={onKeyDown}
        value={orderSummary}
        inputMode="none"
        autoComplete={HTMLAutoCompleteAttribute.Off}
        {...(hasError
          ? {
              'aria-describedby': stateMessagesId,
            }
          : {})}
      />

      {hasError && <StateMessages id={stateMessagesId} errors={errors} hasError={hasError} />}
      <Drawer
        title={t('h24_select_order')}
        isOpen={isOpen}
        onClose={closeDrawer}
        content={
          <OrderList
            orderIdToScroll={value}
            setOrder={setOrder}
            orders={orders}
            isOpen={isOpen}
            isLoading={isLoading}
            onClose={closeDrawer}
            fetchMore={fetchMore}
            hasMoreOrders={hasMoreOrders}
            hasFetchError={hasFetchError}
          />
        }
      />
    </>
  );
};

export default OrderNumberDrawer;
